import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FileText, Sparkles, CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PublicPageLayout } from "@/components/landing/PublicPageLayout";
import { CALENDLY_URL, DEMO_FALLBACK_EMAIL } from "@/lib/demoBooking";
import { trackCtaClick } from "@/lib/ctaTracking";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const agenda = [
  { icon: FileText, title: "Your scattered knowledge", body: "How Sorvaine pulls past answers, case studies, and proven content into one searchable vault — ready to use on any bid." },
  { icon: Sparkles, title: "AI drafting on a live tender", body: "Watch Sorvaine generate a structured first draft from a real tender document, in minutes." },
  { icon: CheckCircle2, title: "Pricing and next steps", body: "Walk through plans, onboarding timeline, and answer any questions on fit." },
];

export default function BookDemo() {
  const calendlyRef = useRef<HTMLDivElement>(null);
  const confirmedRef = useRef(false);

  // Load Calendly inline embed script and (re)initialize on mount
  useEffect(() => {
    const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
    const url = `${CALENDLY_URL}?hide_gdpr_banner=1&hide_event_type_details=1&background_color=0f1115&text_color=ffffff&primary_color=c2541a`;

    const init = () => {
      const w = (window as any).Calendly;
      if (w && calendlyRef.current) {
        calendlyRef.current.innerHTML = "";
        w.initInlineWidget({ url, parentElement: calendlyRef.current });
      }
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (!existing) {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.onload = init;
      document.body.appendChild(s);
    } else if ((window as any).Calendly) {
      init();
    } else {
      existing.addEventListener("load", init);
    }

    // Track demo booking confirmation via Calendly postMessage
    const onMessage = (e: MessageEvent) => {
      try {
        if (!e.origin || !/(^|\.)calendly\.com$/.test(new URL(e.origin).hostname)) return;
        const evt = (e.data as any)?.event;
        if (evt === "calendly.event_scheduled" && !confirmedRef.current) {
          confirmedRef.current = true;
          void trackCtaClick("demo_confirmed", "booking_confirmation");
        }
      } catch {
        // ignore malformed messages
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <PublicPageLayout>
      <SEO
        title="Book a Demo | Sorvaine"
        description="Schedule a 30-minute demo of Sorvaine. Bring a real tender and see how AI-generated drafts speed up your responses."
        canonical="https://sorvaine.com/book-demo"
      />

      <section className="pt-8 md:pt-14 pb-10 md:pb-16 px-6">
        <div className="max-w-[1180px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-6 md:gap-8 lg:gap-12">

            {/* Left — Hero copy + What we'll cover */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="flex flex-col lg:col-span-7"
            >
              <motion.div variants={fadeIn} className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-[11px] tracking-[0.25em] uppercase text-white/50 font-medium">Book a Demo</span>
              </motion.div>

              <motion.h1 variants={fadeIn} className="text-[30px] md:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[1.05] md:leading-[1.0] mb-5 md:mb-6 text-white">
                Book a Sorvaine demo
              </motion.h1>

              <motion.p variants={fadeIn} className="text-white/55 text-base md:text-lg leading-[1.7] mb-8 md:mb-10 max-w-[60ch]">
                30 minutes. See exactly how Sorvaine turns a tender into a structured first draft — faster than anything your team does today.
              </motion.p>

              <motion.div variants={fadeIn} className="space-y-4 md:space-y-5 mb-8 md:mb-10 max-w-[44ch]">
                <h2 className="text-[11px] tracking-[0.25em] uppercase text-white/40 font-medium">What we'll cover</h2>
                {agenda.map((a) => (
                  <div key={a.title} className="flex items-start gap-3">
                    <a.icon className="w-[18px] h-[18px] text-primary shrink-0 mt-1" />
                    <div>
                      <p className="text-white/90 text-base font-medium mb-1">{a.title}</p>
                      <p className="text-white/50 text-[15px] leading-[1.65]">{a.body}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              <motion.p variants={fadeIn} className="text-white/40 text-sm mt-auto">
                Prefer email?{" "}
                <a
                  href={`mailto:${DEMO_FALLBACK_EMAIL}?subject=Sorvaine%20demo%20request`}
                  className="text-white/70 hover:text-white underline underline-offset-4"
                >
                  {DEMO_FALLBACK_EMAIL}
                </a>
              </motion.p>
              <p className="text-white/30 text-sm mt-2">
                Used by bid teams across professional services, consulting, and technology.
              </p>
            </motion.div>

            {/* Right — Calendly inline embed */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-5 lg:mt-[60px]"
            >
              <div className="rounded-2xl landing-card overflow-hidden">
                <div
                  ref={calendlyRef}
                  className="min-w-[320px] h-[480px] md:h-[560px]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PublicPageLayout>
  );
}
